export const DIFFICULTIES = ['trivial', 'easy', 'medium', 'hard'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

/** Seed packets, which grow decorative flowers. */
export const SEED_KINDS = ['clover', 'daisy', 'tulip', 'sunflower'] as const;
export type SeedKind = (typeof SEED_KINDS)[number];
export type SeedMap = Partial<Record<SeedKind, number>>;

/** Companions: three live in a planted tree, three around the raised bed or flower cart. */
export const ANIMAL_KINDS = ['songbird', 'squirrel', 'owl', 'bumblebee', 'toad', 'butterfly'] as const;
export type AnimalKind = (typeof ANIMAL_KINDS)[number];

export const STARTER_BIOMES = ['meadow', 'wetland', 'jungleRuins', 'canyon', 'pineRidge'] as const;
export type StarterBiome = (typeof STARTER_BIOMES)[number];
/** The starters released at launch (decided Oct 11, 2026); wetland and jungle ruins come later. The rules' releasedBiomes can widen this. */
export const LAUNCH_STARTERS = ['meadow', 'pineRidge', 'canyon'] as const satisfies readonly StarterBiome[];
export const SHARED_BIOMES = ['woodlandLake', 'coast', 'mountainSummit', 'sacredGrove'] as const;
export type SharedBiome = (typeof SHARED_BIOMES)[number];
export const BIOMES = [...STARTER_BIOMES, ...SHARED_BIOMES] as const;
export type Biome = (typeof BIOMES)[number];

/** Every plant grows through these; growth comes from total completions. */
export const GROWTH_STAGES = ['seed', 'small', 'medium', 'large', 'mega'] as const;
export type GrowthStage = (typeof GROWTH_STAGES)[number];

export type TreeSpecies = 'birch' | 'maple' | 'oak' | 'pine';

/** A local calendar date, `YYYY-MM-DD`. */
export type DayString = string;

export type Source = 'app' | 'claude' | 'import' | 'system';

/** Sync fields on every stored record, so an offline device can merge later. */
export interface Meta {
  id: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface Pause {
  since: DayString;
  /** First day back; `null` pauses until the player resumes. */
  until: DayString | null;
}

export interface Profile extends Meta {
  timezone: string;
  /** Hour (0–23) when a new day starts, for people who finish things after midnight. */
  dayStartHour: number;
  starterBiome: StarterBiome;
  /** The player picked their starter (or signed up with one); until then the app offers the choice. */
  starterChosen?: boolean;
  /** The player's own changes to each biome's scenery: pieces moved, and pieces put away in the bank. */
  scenes?: Partial<Record<Biome, SceneEdits>>;
  /** Cached totals; the ledger is the source of truth. */
  coins: number;
  xp: number;
  level: number;
  pause: Pause | null;
  /** The last local day the daily reset has run for. */
  lastCronDay: DayString | null;
}

/** The ledger events a completion wrote, so undo can reverse exactly those. */
export interface Pending {
  events: { id: string; coins: number; xp: number; seeds: SeedMap; growth: number; plantId: string | null }[];
}

/**
 * What an undo couldn't take back because it was already spent or planted (balances never go
 * below zero). The next payout from the same task covers it first, so completing and undoing
 * again can't pay twice. Not a balance and never shown as debt: a daily lets it go at the day
 * reset, so new effort always pays in full.
 */
export interface Owed {
  /** The payout event it is still short of reversing. */
  eventId: string;
  coins: number;
  xp: number;
  seeds: SeedMap;
}

interface TaskBase extends Meta {
  text: string;
  notes: string;
  difficulty: Difficulty;
  tags: string[];
  sectionId: string | null;
  position: number;
  /** The task's id in the app it was imported from, so importing again updates it. */
  importId?: string | null;
  archivedAt: string | null;
}

export interface Habit extends TaskBase {
  type: 'habit';
  /** Whether the + and − buttons are offered. */
  up: boolean;
  down: boolean;
  counterUp: number;
  counterDown: number;
}

export type Repeat =
  | { kind: 'weekly'; /** 0 = Sunday … 6 = Saturday */ days: number[] }
  | { kind: 'everyNDays'; every: number; startDate: DayString };

export interface Daily extends TaskBase {
  type: 'daily';
  /** Short name that can be shown on the tree. */
  label: string;
  repeat: Repeat;
  paused: boolean;
  /** Checked off today. */
  completed: boolean;
  pending: Pending | null;
  /** Left over from an undo today; cleared at the day reset. Missing on older saves. */
  owed?: Owed[];
}

export interface Todo extends TaskBase {
  type: 'todo';
  completed: boolean;
  completedAt: string | null;
  dueDate: DayString | null;
  pending: Pending | null;
  /** Left over from an undo; covered when the to-do is completed again. Missing on older saves. */
  owed?: Owed[];
}

export type Task = Habit | Daily | Todo;
export type TaskType = Task['type'];

export interface ChecklistItem extends Meta {
  taskId: string;
  text: string;
  done: boolean;
  position: number;
}

export interface Section extends Meta {
  name: string;
  position: number;
}

export type PlantKind = 'tree' | 'bed' | 'wildflower' | 'flower' | 'weed';

/** Garden data only: a plant never holds task text, so it can be shown to friends. */
export interface Plant extends Meta {
  taskId: string | null;
  kind: PlantKind;
  /** Tree species, flower seed kind, or 'weed'. */
  species: string;
  /** Growth pace, fixed when the plant is made so a tree never shrinks if its task's difficulty changes. */
  pace: Difficulty;
  biome: Biome | null;
  x: number | null;
  y: number | null;
  /** Raised-bed plot for habit plants. */
  plot: number | null;
  stage: GrowthStage;
  completions: number;
  streak: number;
  leaves: 'full' | 'sparse';
  /** Wildflowers: false while a bud. */
  bloomed: boolean;
  /** Days of full bloom left; counts down only on days that aren't paused. */
  bloomDaysLeft: number | null;
  potted: boolean;
  /** Weeds: days old. */
  age: number;
  goneAt: string | null;
  goneReason: 'picked' | 'wentToSeed' | 'pulled' | 'faded' | 'moved' | null;
}

export type InventoryType = 'seed' | 'animal' | 'decoration' | 'cosmetic' | 'pressedFlower';

export interface InventoryItem extends Meta {
  type: InventoryType;
  kind: string;
  quantity: number;
  name: string | null;
  biome: Biome | null;
  x: number | null;
  y: number | null;
  /** Tree dwellers live in a tree plant. */
  homePlantId: string | null;
  /** Pressed flowers remember their to-do. */
  taskId: string | null;
  day: DayString | null;
}

/**
 * Changes a player made to a biome's fixed scenery (logs, stumps, rocks, bushes). Keys are the
 * Lab's piece ids (creation order in its buildScene). `hidden` pieces sit in the bank until put back.
 */
export interface SceneEdits {
  moves: Record<string, { x: number; y: number }>;
  hidden: string[];
}

export interface Unlock {
  biome: Biome;
  unlockedAt: string;
  reason: 'starter' | 'level' | 'megaTree';
}

export interface GameState {
  profile: Profile;
  tasks: Task[];
  checklist: ChecklistItem[];
  sections: Section[];
  /** Active plants; gone ones are kept in storage but needn't be loaded. */
  plants: Plant[];
  inventory: InventoryItem[];
  unlocks: Unlock[];
}

export const LEDGER_TYPES = [
  'complete', 'habitUp', 'habitDown', 'weeklyBonus', 'reverse', 'pick', 'purchase',
  'plantSeed', 'wentToSeed', 'import',
] as const;
export type LedgerType = (typeof LEDGER_TYPES)[number];

/** Append-only. Balances, streaks and growth are caches that can be rebuilt from these. */
export interface LedgerEvent {
  id: string;
  type: LedgerType;
  taskId: string | null;
  plantId: string | null;
  occurredAt: string;
  localDate: DayString;
  source: Source;
  currency: 'coins';
  coins: number;
  xp: number;
  seeds: SeedMap;
  /** Change in the plant's total completions. */
  growth: number;
  reverses: string | null;
  idempotencyKey: string | null;
  rulesVersion: number;
}

export type DayStatus = 'done' | 'notDone' | 'notDue' | 'paused';

/** One per daily per ended day; streaks and catch-up can be rebuilt from these. */
export interface DayRecord {
  taskId: string;
  date: DayString;
  status: DayStatus;
}

/** Things for the app to show; not stored. */
export type Notice =
  | { type: 'reward'; taskId: string; coins: number; xp: number; seeds: SeedMap }
  | { type: 'weeklyBonus'; taskId: string; coins: number; seeds: SeedMap; streak: number }
  | { type: 'rewardReversed'; taskId: string; coins: number; xp: number; seeds: SeedMap }
  | { type: 'levelUp'; level: number }
  | { type: 'biomeUnlocked'; biome: Biome; reason: Unlock['reason'] }
  | { type: 'grew'; plantId: string; taskId: string | null; stage: GrowthStage }
  | { type: 'leavesFull'; plantId: string }
  | { type: 'streakEnded'; plantId: string; streak: number }
  | { type: 'bloomed'; plantId: string }
  | { type: 'wentToSeed'; plantId: string; seed: SeedKind }
  | { type: 'picked'; plantId: string; use: 'coins' | 'bouquet' | 'press'; coins: number }
  | { type: 'weedSprouted'; plantId: string; taskId: string }
  | { type: 'dayStarted'; day: DayString; daysProcessed: number }
  | { type: 'welcomeBack'; daysAway: number; wentToSeed: number }
  | { type: 'planted'; plantId: string; seed: SeedKind }
  | { type: 'animalJoined'; itemId: string; kind: AnimalKind; name: string; homePlantId: string | null };

/** Things the engine needs from the outside world, so it stays deterministic. */
export interface Context {
  now: Date;
  newId: () => string;
  /** Uniform in [0, 1); decides seed drops. */
  random: () => number;
  rules: import('./rules.js').Rules;
  source: Source;
  idempotencyKey?: string;
}
